import React, {
  ReactElement,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import { Content, FlexboxGrid, FormInstance, Text } from 'rsuite';
import {
  selectConfigExpressAllTaxCollectionAgreementWith,
  selectConfigExpressTax,
  selectConfigExpressTaxHasCollectionAgreement,
} from 'src/store/slices/config/configSelectors';
import { TaxReportContent } from 'src/components/tax/TaxReportContent';
import PortalHeader from 'src/components/layout/PortalHeader';
import { FLOWS_ENUM, TAX_PAYMENT_PERIOD_TYPES_ENUM } from 'src/common/enums';
import { constructMarkup } from 'src/common/utils';
import FlowPageLayout from 'src/components/layout/FlowPageLayout';
import CollectionAgreementNotice from 'src/components/tax/CollectionAgreementNotice';
import IconIndicator from 'src/components/user-feedback/IconIndicator';
import Notice from 'src/components/user-feedback/Notice';
import {
  selectCalculatedTaxInProgress,
  selectCalculatedTaxSuccess,
  selectCalculateTaxErrorMsg,
  selectTaxFlowData,
  selectTaxPaymentPeriods,
} from 'src/store/slices/tax/taxSelectors';
import usePlaceNavigate from 'src/hooks/usePlaceNavigate';
import { selectCurrentStep } from 'src/store/slices/flow/flowSelectors';
import {
  setStepCompletedStatus,
  setCurrentStep,
} from 'src/store/slices/flow/flowSlice';
import {
  resetCalculateTax,
  updateTaxFlowData,
} from 'src/store/slices/tax/taxSlice';
import { calculateMultiPropertyTaxAsync } from 'src/store/slices/tax/taxThunks';
import { PeriodAndLicense, TaxDataModel } from 'src/types/tax';
import { TaxConfigurationInformation } from 'src/types/configuration';
import { useAppDispatch } from 'src/store/store';
import { FLOW_DIRECTION } from 'src/types/flow';
import { useIsMobile } from 'src/hooks/useIsMobile';

export const TaxReport = (): ReactElement => {
  const { isMobile } = useIsMobile();
  const dispatch = useAppDispatch();
  const taxForm = useRef<FormInstance>(null);
  const placeNavigate = usePlaceNavigate();
  const [flowDirection, setFlowDirection] = useState<FLOW_DIRECTION>();
  const formData = useSelector(selectTaxFlowData);
  const selectedCurrentStep = useSelector(selectCurrentStep);
  const taxCalculationInProgress = useSelector(selectCalculatedTaxInProgress);
  const taxCalculationSucceeded = useSelector(selectCalculatedTaxSuccess);
  const taxCalculationErrorMessage = useSelector(selectCalculateTaxErrorMsg);
  const expressTax = useSelector(selectConfigExpressTax);
  const taxConfig = expressTax?.tax_config;
  const deductions = expressTax?.deductions;
  const reportRevenueSubHeading =
    expressTax?.report_revenue?.report_page_sub_heading;

  const hasCollectionAgreement = useSelector(
    selectConfigExpressTaxHasCollectionAgreement
  );

  const allCollectionAgreements = useSelector(
    selectConfigExpressAllTaxCollectionAgreementWith
  );

  const taxablePeriod = useSelector(selectTaxPaymentPeriods);

  const selectedLicenses = useMemo(() => {
    return formData.selectedLicenses || [];
  }, [formData?.selectedLicenses]);

  useEffect(() => {
    dispatch(resetCalculateTax());
  }, [dispatch]);

  // We have a use effect which has a dependency which checks if that has been completed and if it has will navigate to the next step
  const handleNextClick = async () => {
    setFlowDirection(FLOW_DIRECTION.FORWARD);
    taxForm.current?.submit();
    return true;
  };

  const handleBackClick = async () => {
    setFlowDirection(FLOW_DIRECTION.BACK);
    taxForm.current?.submit();
    return true;
  };

  const getUpdatedData = (value: TaxDataModel): Array<PeriodAndLicense> => {
    return selectedLicenses.map((period, pIndex) => {
      const formPeriod = value.periods?.[pIndex];
      return {
        ...period,
        licenses: period.licenses.map((license, index) => ({
          ...license,
          occupancy: formPeriod?.licenses?.[index].occupancy,
          buckets: formPeriod?.licenses?.[index].buckets.map((bucket) => ({
            ...bucket,
            revenue: Number(bucket.revenue),
            discounts: bucket.discounts,
          })),
        })),
      };
    });
  };

  const handleSubmit = async (value: TaxDataModel) => {
    dispatch(resetCalculateTax());
    // get updated data and save to redux
    const updatedSelectedLicenses = getUpdatedData(value);
    // wait for the calculations to run before continuing
    if (flowDirection === FLOW_DIRECTION.FORWARD) {
      const calculationSuccessful = await dispatch(
        await calculateMultiPropertyTaxAsync(
          updatedSelectedLicenses,
          taxConfig as TaxConfigurationInformation[]
        )
      );

      if (calculationSuccessful) {
        dispatch(
          updateTaxFlowData({ selectedLicenses: updatedSelectedLicenses })
        );

        dispatch(
          setStepCompletedStatus({ stepNum: selectedCurrentStep, status: true })
        );
        dispatch(setCurrentStep(selectedCurrentStep + 1));
        placeNavigate('/tax-confirm-and-sign');
      }
    } else {
      dispatch(
        updateTaxFlowData({ selectedLicenses: updatedSelectedLicenses })
      );
    }
  };

  const taxablePeriodsText = useMemo(() => {
    if (reportRevenueSubHeading) {
      return reportRevenueSubHeading;
    } else {
      const periodString =
        taxablePeriod === TAX_PAYMENT_PERIOD_TYPES_ENUM.QUARTERLY
          ? 'quarters'
          : 'months';
      return `For the current and previous ${periodString}, please report the taxable receipts.`;
    }
  }, [taxablePeriod, reportRevenueSubHeading]);

  return (
    <FlexboxGrid justify="space-between">
      <FlexboxGrid.Item colspan={24}>
        <PortalHeader flow={FLOWS_ENUM.PAY_TOT} />
      </FlexboxGrid.Item>
      <FlexboxGrid.Item colspan={24}>
        <Content>
          <FlowPageLayout
            title="Report Revenue"
            onNext={handleNextClick}
            onBack={handleBackClick}
            subtitle=""
            omitNavigationForward
          >
            <Text className="text-large">{taxablePeriodsText}</Text>
            <Text className="paragraph text-small mb-24">
              {constructMarkup(expressTax?.report_revenue?.text ?? '')}
            </Text>
            {hasCollectionAgreement && <CollectionAgreementNotice />}
            <TaxReportContent
              buckets={allCollectionAgreements}
              deductions={deductions}
              isMobile={isMobile}
              licenses={selectedLicenses}
              onSubmit={handleSubmit}
              ref={taxForm}
            />

            {!taxCalculationSucceeded && taxCalculationErrorMessage && (
              <Notice message={taxCalculationErrorMessage} />
            )}
            {taxCalculationInProgress && <IconIndicator />}
          </FlowPageLayout>
        </Content>
      </FlexboxGrid.Item>
    </FlexboxGrid>
  );
};
